/**
 * UnifiedArticleDetailPage.mjs
 * 
 * Polymorphic article detail page that detects article type and renders
 * the appropriate component (ArticleDetail for generated, SportsArticleDetailPage for streamlined).
 * 
 * This component handles client-side routing where we need to detect article type
 * from the Redux state or fetch it first.
 * 
 * IMPORTANT: For SSR hydration to work correctly, this component must check Redux state
 * synchronously BEFORE rendering any loading indicators to avoid hydration mismatch.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';

// Import both article detail components
import ArticleDetail from './ArticleDetail.mjs';
import SportsArticleDetailPage from './SportsArticleDetailPage.mjs';

// Import the thunks for fetching articles
import { fetchAndAddArticle } from '../slices/siteArticlesSlice.mjs';
import { fetchArticleById, setCurrentArticle } from '../slices/unifiedArticlesSlice.mjs';

/**
 * UnifiedArticleDetailPage - Routes to the correct article component
 * based on article type detection.
 * 
 * Priority:
 * 1. Check if article already in unifiedArticles state (from SSR preload)
 * 2. Check if article already in siteArticles state (from SSR preload)
 * 3. If not found, detect type via API calls
 */
const UnifiedArticleDetailPage = ({ slugOverride } = {}) => {
    const { slug: paramSlug } = useParams();
    const slug = slugOverride || paramSlug;
    const dispatch = useDispatch();

    // Check Redux state SYNCHRONOUSLY for SSR hydration
    const streamlinedCurrentArticle = useSelector(state => state.unifiedArticles?.currentArticle);
    const streamlinedArticleInList = useSelector(state => {
        // Search in articlesByScope (map of scopeKey -> articles array)
        const byScope = state.unifiedArticles?.articlesByScope || {};
        for (const scopeKey of Object.keys(byScope)) {
            const scopeArticles = byScope[scopeKey];
            if (Array.isArray(scopeArticles)) {
                for (const group of scopeArticles) {
                    if (group?.articles) {
                        const found = group.articles.find(a => a.slug === slug);
                        if (found) return found;
                    }
                }
            }
        }

        // Check landing page article cache
        const landingArticles = state.landing?.articles;
        if (Array.isArray(landingArticles)) {
            for (const group of landingArticles) {
                if (group?.articles) {
                    const found = group.articles.find(a => a.slug === slug);
                    if (found) return found;
                }
            }
        }

        // Check forYou feed section cache
        const sectionData = state.forYou?.feed?.sectionData;
        if (sectionData) {
            for (const sectionId of Object.keys(sectionData)) {
                const section = sectionData[sectionId];
                if (Array.isArray(section?.articles)) {
                    const found = section.articles.find(a => a.slug === slug);
                    if (found) return found;
                }
            }
        }

        return null;
    });
    // streamlinedArticleInCategory is now merged into streamlinedArticleInList
    const streamlinedArticleInCategory = null;

    const generatedCurrentArticle = useSelector(state => state.siteArticles?.currentArticle);
    const generatedArticleInList = useSelector(state =>
        state.siteArticles?.articles?.find(a => a.slug === slug)
    );

    // When we find the article in a list cache but it's not set as currentArticle,
    // dispatch it so SportsArticleDetailPage can render immediately without fetching.
    useEffect(() => {
        if (streamlinedArticleInList && (!streamlinedCurrentArticle || streamlinedCurrentArticle.slug !== slug)) {
            dispatch(setCurrentArticle(streamlinedArticleInList));
        }
    }, [slug, streamlinedArticleInList, streamlinedCurrentArticle, dispatch]);

    // Determine article type synchronously from Redux state
    const detectedType = useMemo(() => {
        // SSR case: If slug is undefined but currentArticle exists, use that
        // This handles SSR where useParams() doesn't work but controller preloaded article
        if (!slug) {
            if (streamlinedCurrentArticle) {
                return 'streamlined';
            }
            if (generatedCurrentArticle) {
                return 'generated';
            }
        }

        // Check if this article is the current streamlined article (match by slug)
        if (streamlinedCurrentArticle && streamlinedCurrentArticle.slug === slug) {
            return 'streamlined';
        }

        // Check if article exists in streamlined articles list or category data
        if (streamlinedArticleInList || streamlinedArticleInCategory) {
            return 'streamlined';
        }

        // Check if this article is the current generated article (match by slug)
        if (generatedCurrentArticle && generatedCurrentArticle.slug === slug) {
            return 'generated';
        }

        // Check if article exists in generated articles list
        if (generatedArticleInList) {
            return 'generated';
        }

        // Unknown - needs async detection
        return null;
    }, [slug, streamlinedCurrentArticle, streamlinedArticleInList, streamlinedArticleInCategory,
        generatedCurrentArticle, generatedArticleInList]);

    // State for async detection when article not in Redux
    const [asyncDetectedType, setAsyncDetectedType] = useState(null);
    const [isDetecting, setIsDetecting] = useState(false);
    const [detectionError, setDetectionError] = useState(null);

    // Async detection effect - only runs when article not already in state
    useEffect(() => {
        const detectAndFetch = async () => {
            // Skip if we already have type from Redux
            if (detectedType) return;

            // Skip if already detecting or detected
            if (isDetecting || asyncDetectedType) return;

            setIsDetecting(true);
            setDetectionError(null);

            try {
                // Try streamlined first - use slug-based lookup
                try {
                    const streamlinedResponse = await axios.get(`/q/api/streamlined-articles/by-slug/${slug}`);
                    if (streamlinedResponse.data?.data) {
                        // Dispatch to set current article in Redux
                        dispatch(setCurrentArticle(streamlinedResponse.data.data));
                        setAsyncDetectedType('streamlined');
                        setIsDetecting(false);
                        return;
                    }
                } catch (e) {
                    // Not found in streamlined, try generated
                }

                // Try generated - use slug-based lookup
                try {
                    const generatedResponse = await axios.get(`/q/api/article-api/by-slug/${slug}`);
                    if (generatedResponse.data) {
                        dispatch(fetchAndAddArticle(slug));
                        setAsyncDetectedType('generated');
                        setIsDetecting(false);
                        return;
                    }
                } catch (e) {
                    // Not found in either
                }

                // Article not found
                setDetectionError('Article not found');
                setIsDetecting(false);

            } catch (error) {
                console.error('Error detecting article type:', error);
                setDetectionError('Error loading article');
                setIsDetecting(false);
            }
        };

        if (slug && !detectedType) {
            detectAndFetch();
        }
    }, [slug, detectedType, isDetecting, asyncDetectedType, dispatch]);

    // Final resolved type
    const finalType = detectedType || asyncDetectedType;

    // If article type is known (from Redux), render immediately for SSR hydration
    if (finalType === 'streamlined') {
        return <SportsArticleDetailPage />;
    }

    if (finalType === 'generated') {
        return <ArticleDetail />;
    }

    // Only show loading if truly detecting asynchronously
    if (isDetecting) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-theme-surface">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary mx-auto mb-4"></div>
                    <p className="text-theme-text-muted">Loading article...</p>
                </div>
            </div>
        );
    }

    // Show error state
    if (detectionError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-theme-surface">
                <div className="text-center py-20">
                    <h2 className="text-xl font-semibold text-theme-text mb-2">
                        {detectionError}
                    </h2>
                    <p className="text-theme-text-muted mb-4">
                        The requested article could not be found.
                    </p>
                    <a href="/" className="text-theme-link hover:underline">← Back to Home</a>
                </div>
            </div>
        );
    }

    // Fallback - shouldn't reach here normally, but show loading as safety
    return (
        <div className="min-h-screen flex items-center justify-center bg-theme-surface">
            <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary mx-auto mb-4"></div>
                <p className="text-theme-text-muted">Loading article...</p>
            </div>
        </div>
    );
};

export default UnifiedArticleDetailPage;
